import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { LoadingState, EmptyState, TableRowActions, downloadIcon, viewIcon } from '@tpct/admin-ui';
import type { TableRowAction } from '@tpct/admin-ui';
import { __, sprintf } from '@/hub-shared/utils/i18n';
import { fetchSubscriptionBilling, postCancelPendingPlanChange, postCancelSubscriptionAtPeriodEnd } from '../api/subscriptionBilling';
import { fetchRewardStatus, postRedeemRewardCode } from '../api/rewards';
import { errorToUserFacingMessage } from '../api/apiErrors';
import type { PortalBootstrapData } from '../types/window';
import type { SubscriptionBillingPayload } from '../types/subscriptionBilling';
import type { RewardStatusResponse } from '../api/rewards';
import type { PortalSearchSyncFn } from '../utils/portalUrlState';

type SubscriptionTabProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
  checkoutStatus?: 'success' | 'cancelled' | null;
  patchPortalSearch?: PortalSearchSyncFn;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'forbidden';

const REPORTS_USED_KEY = 'reports.distinct_in_subscription_year';
const REPORTS_MAX_KEY = 'reports.max_per_subscription_year';

const formatAmount = (amount: number | null, currency: string | null): string => {
  if (amount === null) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency ?? 'EUR' }).format(amount);
};

const formatDate = (d: string | null): string => {
  if (!d) return '—';
  try {
    const [y, m, day] = d.split('-').map(Number);
    const date = new Date(y, m - 1, day);
    if (Number.isNaN(date.getTime())) return d;
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(date);
  } catch {
    return d;
  }
};

const formatInvoiceReference = (invoice: SubscriptionBillingPayload['invoices'][number]): string => {
  if (invoice.documentSource === 'stripe_billing' && typeof invoice.purchaseId === 'number' && invoice.purchaseId > 0) {
    return sprintf('%s %d', __('Achat n°'), invoice.purchaseId);
  }

  if (typeof invoice.orderId === 'number' && invoice.orderId > 0) {
    return sprintf('%s %d', __('Commande n°'), invoice.orderId);
  }

  if (typeof invoice.purchaseId === 'number' && invoice.purchaseId > 0) {
    return sprintf('%s %d', __('Achat n°'), invoice.purchaseId);
  }

  const matched = /^(order|purchase):(\d+)$/.exec(invoice.id);
  if (matched) {
    return matched[1] === 'order'
      ? sprintf('%s %d', __('Commande n°'), Number(matched[2]))
      : sprintf('%s %d', __('Achat n°'), Number(matched[2]));
  }

  return invoice.id;
};

const invoiceActionFallback = (documentState?: string): string => {
  if (documentState === 'generating') return __('Facture en cours de génération');
  return '—';
};

const SubscriptionTab = ({ bootstrap, onSessionExpired, checkoutStatus, patchPortalSearch }: SubscriptionTabProps) => {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [error, setError] = useState('');
  const [data, setData] = useState<SubscriptionBillingPayload | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCancelPendingModal, setShowCancelPendingModal] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelPendingSubmitting, setCancelPendingSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelPendingError, setCancelPendingError] = useState('');
  const [rewardState, setRewardState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [rewardData, setRewardData] = useState<RewardStatusResponse | null>(null);
  const [rewardError, setRewardError] = useState('');
  const [rewardCode, setRewardCode] = useState('');
  const [rewardSubmitting, setRewardSubmitting] = useState(false);
  const [rewardSuccess, setRewardSuccess] = useState('');

  const load = useCallback(async () => {
    setLoadState('loading');
    setError('');
    try {
      const result = await fetchSubscriptionBilling({
        baseUrl: bootstrap.restUrls.accountingHub,
        nonce: bootstrap.nonce,
      });
      if (result.ok) {
        setData(result.data);
        setLoadState('ready');
        if (checkoutStatus === 'success' && typeof patchPortalSearch === 'function') {
          patchPortalSearch({ checkout: null, session_id: null }, 'replace');
        }
      } else if (result.status === 401) {
        onSessionExpired();
      } else if (result.status === 403) {
        setLoadState('forbidden');
      } else {
        setError(result.message || __('Erreur de chargement'));
        setLoadState('error');
      }
    } catch (e) {
      setError(errorToUserFacingMessage(e, __('Erreur de chargement')));
      setLoadState('error');
    }
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, checkoutStatus, onSessionExpired, patchPortalSearch]);

  const loadRewards = useCallback(async () => {
    setRewardState('loading');
    setRewardError('');
    try {
      const result = await fetchRewardStatus({
        baseUrl: bootstrap.restUrls.accountingHub,
        nonce: bootstrap.nonce,
      });
      if (result.ok) {
        setRewardData(result.data);
        setRewardState('ready');
      } else if (result.status === 401) {
        onSessionExpired();
      } else if (result.status === 403) {
        setRewardError(__('Accès refusé.'));
        setRewardState('error');
      } else {
        setRewardError(result.message || __('Erreur de chargement'));
        setRewardState('error');
      }
    } catch (e) {
      setRewardError(errorToUserFacingMessage(e, __('Erreur de chargement')));
      setRewardState('error');
    }
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, onSessionExpired]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadRewards();
  }, [loadRewards]);

  if (loadState === 'idle' || loadState === 'loading') {
    return <LoadingState columns={[__('Section'), __('Valeur')]} className="subscription-tab-loading" />;
  }

  if (loadState === 'error') {
    return (
      <div className="subscription-tab subscription-tab--error container-style">
        <p className="accounting-hub-notice accounting-hub-notice-error">{error}</p>
        <button type="button" className="btn-primary" onClick={() => void load()}>
          {__('Réessayer')}
        </button>
      </div>
    );
  }

  if (loadState === 'forbidden') {
    return (
      <div className="subscription-tab container-style">
        <p className="accounting-hub-notice accounting-hub-notice-warning">
          {__('Vous n’avez pas la permission de consulter la facturation du cabinet.')}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { subscription, payment, invoices, orders } = data;
  const isOwner = bootstrap.isSubscriptionOwner === true;

  const ent = bootstrap.entitlements;
  const usedReports = typeof ent?.usage[REPORTS_USED_KEY] === 'number' ? ent.usage[REPORTS_USED_KEY] : null;
  const maxReports = typeof ent?.limits[REPORTS_MAX_KEY] === 'number' ? ent.limits[REPORTS_MAX_KEY] : null;
  const progressPct =
    usedReports !== null && maxReports !== null && maxReports > 0
      ? Math.min(100, Math.round((usedReports / maxReports) * 100))
      : null;

  const orderViewUrls = new Map(orders.map((o) => [o.id, o.viewUrl]));
  const invoicePending =
    subscription.source === 'stripe_billing'
    && subscription.hasSuccessfulPayment === false
    && invoices.length === 0;

  return (
    <div className="subscription-tab">
      {checkoutStatus === 'success' && (
        <div className="accounting-hub-notice accounting-hub-notice-info" role="status" aria-live="polite">
          <strong>{__('Paiement en cours de validation')}</strong>
          <p>
            {__(
              "Votre demande de prélèvement SEPA a bien été reçue. Le paiement est en cours de traitement (délai habituel : 1 à 3 jours ouvrés). Vous recevrez un email de confirmation une fois le paiement validé."
            )}
          </p>
        </div>
      )}
      {checkoutStatus === 'cancelled' && (
        <div className="accounting-hub-notice accounting-hub-notice-warning" role="status" aria-live="polite">
          {__('Le paiement a été annulé. Vous pouvez réessayer depuis la page des forfaits.')}
          {isOwner && (
            <p>
              <a href={bootstrap.subscriptionTierUrl} rel="noopener noreferrer">{__('Voir les forfaits')}</a>
            </p>
          )}
        </div>
      )}

      {data.pendingPlanChange && (
        <div className="accounting-hub-notice accounting-hub-notice-info" role="status" aria-live="polite">
          {data.pendingPlanChange.direction === 'downgrade' ? (
            <>
              <strong>{__('Changement de forfait programmé')}</strong>
              <p className="mt-2">
                {data.pendingPlanChange.currentPlanLabel && data.pendingPlanChange.effectiveDate
                  ? (
                    <>
                      {__('Vous conservez le forfait ')}
                      <strong>{data.pendingPlanChange.currentPlanLabel}</strong>
                      {` ${__('jusqu’au')} ${formatDate(data.pendingPlanChange.effectiveDate)}, ${__('puis passage au forfait ')}`}
                      <strong>{data.pendingPlanChange.targetPlanLabel ?? data.pendingPlanChange.targetPlanId ?? '—'}</strong>
                      .
                    </>
                  )
                  : (
                    <>
                      {__('À la date indiquée, votre abonnement passera au forfait ')}
                      <strong>{data.pendingPlanChange.targetPlanLabel ?? data.pendingPlanChange.targetPlanId ?? '—'}</strong>
                      {typeof data.pendingPlanChange.effectiveDate === 'string' && data.pendingPlanChange.effectiveDate !== ''
                        ? ` (${__('effet prévu :')} ${formatDate(data.pendingPlanChange.effectiveDate)})`
                        : ''}
                    </>
                  )}
              </p>
              {data.pendingPlanChange.consequences.length > 0 && (
                <ul className="mt-2 list-disc pl-5">
                  {data.pendingPlanChange.consequences.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
              {data.pendingPlanChange.cancelSupported && isOwner && (
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn-secondary w-fit"
                    onClick={() => { setCancelPendingError(''); setShowCancelPendingModal(true); }}
                  >
                    {__('Annuler le changement de forfait')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <strong>{__('Surclassement en cours')}</strong>
              <p className="mt-2">
                {__(
                  'Finalisez si besoin la facture de régularisation Stripe (vous pouvez suivre vos factures dans la liste ci-dessous lorsque Stripe les a émises).',
                )}
              </p>
            </>
          )}
        </div>
      )}

      <div className="subscription-tab__plan container-style">
          <div className="outline">
            <span className="subscription-tab__plan-eyebrow">{__('Votre plan')}</span>
            <div className="subscription-tab__plan-row">
              <h3 className="subscription-tab__plan-name">{data.plan?.label ?? __('Plan actif')}</h3>
              <span className="subscription-tab__plan-price">
                {`${formatAmount(subscription.annualPriceExTax, subscription.currency)} / ${__('an')}`}
              </span>
            </div>

            {progressPct !== null && usedReports !== null && maxReports !== null && (
            <div className="subscription-tab__usage">
              <div className="subscription-tab__usage-progress">
                <span className="description">
                  {`${usedReports} ${__('rapports sur')} ${maxReports} ${__('disponibles')}`}
                </span>
                <div
                  className="subscription-tab__progress"
                  role="progressbar"
                  aria-valuenow={progressPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="subscription-tab__progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              <a href={bootstrap.subscriptionTierUrl} className="btn-primary w-fit" rel="noopener noreferrer">
                {__('Comparer les plans')}
              </a>
            </div>
            )}
          </div>

        <div className="subscription-tab__next-billing outline">
          {subscription.nextPaymentDate && (
            <p className="subscription-tab__next-billing-date">
              {`${__('Prochain paiement le')} ${formatDate(subscription.nextPaymentDate)}`}
            </p>
          )}
          <p>{payment.methodLabel ?? __('Mode de paiement non renseigné')}</p>
          {payment.changePaymentMethodSupported && payment.changePaymentMethodUrl && (
            <a
              href={payment.changePaymentMethodUrl}
              className="btn-secondary w-fit"
              target="_blank"
              rel="noopener noreferrer"
            >
              {__('Changer de mode de paiement')}
            </a>
          )}
        </div>
      </div>

      <div className="subscription-tab__section container-style">
        <h3 className="h4">{__('Historique de facturations')}</h3>
        {invoices.length === 0 ? (
          invoicePending ? (
            <div className="accounting-hub-notice accounting-hub-notice-info" role="status" aria-live="polite">
              <strong>{__('Facture en cours')}</strong>
              <p>
                {__(
                  'Votre prélèvement SEPA est en cours de validation (délai habituel : 1 à 3 jours ouvrés). Votre facture apparaîtra ici dès confirmation du paiement.'
                )}
              </p>
            </div>
          ) : (
            <EmptyState
              title={__('Aucune facture')}
              description={__('Les factures apparaîtront ici.')}
            />
          )
        ) : (
          <table className="thn-table subscription-tab__table">
            <thead>
              <tr>
                <th>{__('Référence')}</th>
                <th>{__('Date')}</th>
                <th>{__('Montant')}</th>
                <th>{__('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const viewUrl = inv.orderId > 0 ? (orderViewUrls.get(inv.orderId) ?? null) : null;
                const actions: TableRowAction[] = [];
                if (viewUrl) {
                  actions.push({
                    key: 'view',
                    label: __('Voir'),
                    icon: viewIcon,
                    onClick: () => { window.open(viewUrl, '_blank', 'noreferrer'); },
                  });
                }
                if (inv.downloadSupported && inv.downloadUrl) {
                  actions.push({
                    key: 'download',
                    label: __('Télécharger'),
                    icon: downloadIcon,
                    onClick: () => { window.open(inv.downloadUrl!, '_blank', 'noreferrer'); },
                  });
                }
                return (
                  <tr key={inv.id}>
                    <td>{formatInvoiceReference(inv)}</td>
                    <td>{formatDate(inv.date)}</td>
                    <td>{formatAmount(inv.amount, subscription.currency)}</td>
                    <td className="subscription-tab__actions">
                      {actions.length > 0 ? (
                        <TableRowActions actions={actions} overflowLabel={__('Plus d’actions')} />
                      ) : (
                        <span className="description">{invoiceActionFallback(inv.documentState)}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="subscription-tab__section container-style subscription-tab__reward">
        <h3 className="h4">{__('Code cadeau')}</h3>
        {rewardState === 'loading' ? (
          <p className="description">{__('Chargement des avantages…')}</p>
        ) : (
          <>
            {rewardData && (() => {
              const statusParts = [
                rewardData.generationCreditsAvailable > 0
                  ? `${rewardData.generationCreditsAvailable} ${__('génération gratuite disponible')}`
                  : null,
                rewardData.pendingStripeDiscount?.stripeCouponId
                  ? __('remise Stripe prête pour le premier paiement')
                  : null,
              ].filter((part): part is string => part !== null);

              return statusParts.length > 0 ? (
                <p className="description">{statusParts.join(' · ')}</p>
              ) : null;
            })()}
            {rewardError && <p className="accounting-hub-notice accounting-hub-notice-error">{rewardError}</p>}
            {rewardSuccess && <p className="accounting-hub-notice accounting-hub-notice-success">{rewardSuccess}</p>}
            <div className="subscription-tab__reward-form">
              <label className="description" htmlFor="reward-code-input">
                {__('Entrer un code')}
              </label>
              <div className="subscription-tab__reward-row">
                <input
                  id="reward-code-input"
                  type="text"
                  value={rewardCode}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setRewardCode(e.target.value)}
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="btn-primary subscription-tab__reward-submit"
                  disabled={rewardSubmitting || rewardCode.trim() === ''}
                  onClick={() => {
                    setRewardSubmitting(true);
                    setRewardError('');
                    setRewardSuccess('');
                    void (async () => {
                      const result = await postRedeemRewardCode({
                        baseUrl: bootstrap.restUrls.accountingHub,
                        nonce: bootstrap.nonce,
                        code: rewardCode,
                      });
                      setRewardSubmitting(false);
                      if (result.ok) {
                        setRewardCode('');
                        setRewardSuccess(__('Code appliqué.'));
                        await loadRewards();
                        return;
                      }
                      if (result.status === 401) {
                        onSessionExpired();
                        return;
                      }
                      if (result.status === 403) {
                        setRewardError(__('Accès refusé.'));
                        return;
                      }
                      setRewardError(result.message || __('Le code est invalide.'));
                    })();
                  }}
                >
                  {rewardSubmitting ? __('Application…') : __('Appliquer')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {isOwner &&
        subscription.source === 'stripe_billing' &&
        (subscription.cancelAtPeriodEnd || subscription.selfServeCancellationSupported) && (
          <section className="subscription-tab__section container-style">
            <h3 className="h4">{__('Résilier son abonnement')}</h3>
            {subscription.cancelAtPeriodEnd ? (
              <p className="description" role="status">
                {__(
                  "La résiliation en fin de période est déjà programmée. Vous conservez l'accès au service jusqu'à la date indiquée ci-dessus."
                )}
              </p>
            ) : (
              <>
                <p className="description">
                  {__(
                    "Vous pouvez arrêter le renouvellement automatique. Vous gardez l'accès jusqu'à la fin de la période déjà payée."
                  )}
                </p>
                <button
                  type="button"
                  className="btn-secondary w-fit"
                  onClick={() => { setCancelError(''); setShowCancelModal(true); }}
                >
                  {__("Résilier l'abonnement")}
                </button>
              </>
            )}
          </section>
        )}

      {showCancelPendingModal && (
        <div
          className="thn-modal-overlay"
          role="presentation"
          onClick={() => { if (!cancelPendingSubmitting) setShowCancelPendingModal(false); }}
        >
          <div
            className="thn-modal thn-modal--small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-pending-plan-change-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="thn-modal__close"
              onClick={() => { if (!cancelPendingSubmitting) setShowCancelPendingModal(false); }}
              aria-label={__('Fermer')}
            >
              <span aria-hidden>×</span>
            </button>
            <div className="thn-modal__header">
              <h3 id="cancel-pending-plan-change-modal-title">{__('Annuler le changement programmé')}</h3>
            </div>
            <div className="thn-modal__body">
              <p>
                {__(
                  'Votre forfait actuel sera conservé au prochain renouvellement. Vous pourrez choisir un autre forfait ensuite depuis la page des tarifs.',
                )}
              </p>
              {cancelPendingError && (
                <p className="accounting-hub-notice accounting-hub-notice-error" role="alert">
                  {cancelPendingError}
                </p>
              )}
            </div>
            <div className="thn-modal__footer">
              <button type="button" className="btn-secondary" disabled={cancelPendingSubmitting} onClick={() => setShowCancelPendingModal(false)}>
                {__('Retour')}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={cancelPendingSubmitting}
                onClick={() => {
                  setCancelPendingSubmitting(true);
                  setCancelPendingError('');
                  void (async () => {
                    const result = await postCancelPendingPlanChange({
                      baseUrl: bootstrap.restUrls.accountingHub,
                      nonce: bootstrap.nonce,
                    });
                    setCancelPendingSubmitting(false);
                    if (result.ok) {
                      setShowCancelPendingModal(false);
                      await load();
                      return;
                    }
                    if (result.status === 401) { onSessionExpired(); return; }
                    if (result.status === 403) { setCancelPendingError(__('Accès refusé.')); return; }
                    setCancelPendingError(result.message || __('La demande a échoué.'));
                  })();
                }}
              >
                {cancelPendingSubmitting ? __('En cours…') : __('Confirmer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div
          className="thn-modal-overlay"
          role="presentation"
          onClick={() => { if (!cancelSubmitting) setShowCancelModal(false); }}
        >
          <div
            className="thn-modal thn-modal--small"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-subscription-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="thn-modal__close"
              onClick={() => { if (!cancelSubmitting) setShowCancelModal(false); }}
              aria-label={__('Fermer')}
            >
              <span aria-hidden>×</span>
            </button>
            <div className="thn-modal__header">
              <h3 id="cancel-subscription-modal-title">{__('Confirmer la résiliation')}</h3>
            </div>
            <div className="thn-modal__body">
              <p>
                {__(
                  "Le renouvellement automatique sera arrêté. Vous restez abonné jusqu'à la fin de la période en cours, puis l'accès prendra fin."
                )}
              </p>
              {cancelError && (
                <p className="accounting-hub-notice accounting-hub-notice-error" role="alert">
                  {cancelError}
                </p>
              )}
            </div>
            <div className="thn-modal__footer">
              <button type="button" className="btn-secondary" disabled={cancelSubmitting} onClick={() => setShowCancelModal(false)}>
                {__('Annuler')}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={cancelSubmitting}
                onClick={() => {
                  setCancelSubmitting(true);
                  setCancelError('');
                  void (async () => {
                    const result = await postCancelSubscriptionAtPeriodEnd({
                      baseUrl: bootstrap.restUrls.accountingHub,
                      nonce: bootstrap.nonce,
                    });
                    setCancelSubmitting(false);
                    if (result.ok) {
                      setShowCancelModal(false);
                      await load();
                      return;
                    }
                    if (result.status === 401) { onSessionExpired(); return; }
                    if (result.status === 403) { setCancelError(__('Accès refusé.')); return; }
                    setCancelError(result.message || __('La demande a échoué.'));
                  })();
                }}
              >
                {cancelSubmitting ? __('En cours…') : __('Confirmer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionTab;
